import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { transcriptionsApi, MeetingTranscriptionItem, distributionApi } from '../services/api';
import { websocketService } from '../services/websocket';
import { APP_TITLE } from '../constants';
import TranscriptList from './TranscriptList';
import TranscriptView from './TranscriptView';
import ResizableSplitter from './ResizableSplitter';

const Dashboard: React.FC = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [transcripts, setTranscripts] = useState<MeetingTranscriptionItem[]>([]);
  const [selectedTranscriptId, setSelectedTranscriptId] = useState<string | null>(null);
  const [selectedTranscript, setSelectedTranscript] = useState<MeetingTranscriptionItem | null>(null);
  const [selectedTranscriptVersion, setSelectedTranscriptVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [installers, setInstallers] = useState<{ id: string; platform: string; installerType: string; fileName: string }[]>([]);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Refs so WebSocket callbacks always see latest selection (avoid stale closure)
  const selectionRef = useRef({ selectedTranscriptId, selectedTranscript });
  useEffect(() => {
    selectionRef.current = { selectedTranscriptId, selectedTranscript };
  }, [selectedTranscriptId, selectedTranscript]);

  const loadTranscripts = useCallback(async (showLoading = false, skipAutoSelect = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const loadedTranscripts = await transcriptionsApi.list();
      // Sort by createdAt descending (newest first)
      const sorted = loadedTranscripts.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setTranscripts(sorted);

      if (sorted.length === 0) {
        setSelectedTranscriptId(null);
        setSelectedTranscript(null);
      } else if (!skipAutoSelect && !selectedTranscriptId) {
        // Select first completed transcript if available and none selected
        const firstCompleted = sorted.find(t => t.status === 'completed');
        if (firstCompleted) {
          setSelectedTranscriptId(firstCompleted.id);
          setSelectedTranscript(firstCompleted);
        }
      }
    } catch (error) {
      console.error('Failed to load transcripts:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [selectedTranscriptId]);

  useEffect(() => {
    if (!token) return;

    // Connect WebSocket for real-time transcript updates
    websocketService.connect(
      token,
      () => {}, // onNewInterview (not used)
      () => {}, // onInterviewUpdate (not used)
      undefined, // onInterviewDeleted (not used)
      undefined, // onMessageStreaming (not used)
      (newTranscript: MeetingTranscriptionItem) => {
        console.log('📥 Dashboard: Handling new transcript', newTranscript.id);
        // Add new transcript to list (at the beginning, newest first)
        setTranscripts((prevTranscripts) => {
          const exists = prevTranscripts.find((t) => t.id === newTranscript.id);
          if (exists) {
            // Update existing transcript
            return prevTranscripts.map((t) => (t.id === newTranscript.id ? newTranscript : t));
          }
          // Add new transcript at the beginning
          return [newTranscript, ...prevTranscripts];
        });
      },
      (updatedTranscript: MeetingTranscriptionItem) => {
        console.log('🔄 Dashboard: Handling transcript update', updatedTranscript.id, 'status=', updatedTranscript.status);
        // Update transcript in list (for any status change: capturing → uploading_done → processing → completed/failed/cancelled)
        setTranscripts((prevTranscripts) =>
          prevTranscripts.map((t) => (t.id === updatedTranscript.id ? updatedTranscript : t))
        );
        // Read latest selection from ref so we see current state, not stale closure
        const { selectedTranscriptId: currentId, selectedTranscript: currentSelected } = selectionRef.current;
        if (currentId === updatedTranscript.id) {
          setSelectedTranscript(updatedTranscript);
          setSelectedTranscriptVersion((v) => v + 1);
          if (updatedTranscript.status === 'completed' && !currentSelected) {
            setSelectedTranscriptId(updatedTranscript.id);
            setSelectedTranscript(updatedTranscript);
          }
        } else if (updatedTranscript.status === 'completed' && !currentId) {
          setSelectedTranscriptId(updatedTranscript.id);
          setSelectedTranscript(updatedTranscript);
        }
      },
    );

    // Load transcripts initially
    loadTranscripts(true);

    // Check if desktop app is available
    checkDesktopAppAvailability();

    // Don't disconnect on unmount - keep connection alive across page navigations
    return () => {
      // Just clear callbacks, don't disconnect
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checkDesktopAppAvailability = async () => {
    try {
      const status = await distributionApi.getStatus();
      setInstallers(status.installers ?? []);
    } catch (error) {
      console.error('Failed to check desktop app availability:', error);
      setInstallers([]);
    }
  };

  const handleDownloadDesktopApp = async (installerId: string) => {
    try {
      setDownloadingId(installerId);
      const { downloadUrl, fileName } = await distributionApi.getDownloadLink(installerId);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error('Failed to download desktop app:', error);
      alert(error.response?.data?.message || 'Failed to download desktop app. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const getInstallerButtonLabel = (platform: string) => {
    if (platform === 'mac') return 'macOS';
    if (platform === 'windows') return 'Windows';
    if (platform === 'linux') return 'Linux';
    return 'Download';
  };


  const handleSelectTranscript = (transcriptId: string) => {
    const transcript = transcripts.find(t => t.id === transcriptId);
    // Only allow selecting completed transcripts
    if (transcript && transcript.status === 'completed') {
      setSelectedTranscriptId(transcriptId);
      setSelectedTranscript(transcript);
      setSelectedTranscriptVersion((v) => v + 1);
    }
  };

  const handleDeleteTranscript = async (transcriptId: string) => {
    try {
      await transcriptionsApi.delete(transcriptId);
      // Remove from list
      setTranscripts(prevTranscripts => prevTranscripts.filter(t => t.id !== transcriptId));
      // Clear selection if deleted transcript was selected
      if (selectedTranscriptId === transcriptId) {
        setSelectedTranscriptId(null);
        setSelectedTranscript(null);
        // Auto-select first completed transcript if available
        const remaining = transcripts.filter(t => t.id !== transcriptId);
        const firstCompleted = remaining.find(t => t.status === 'completed');
        if (firstCompleted) {
          setSelectedTranscriptId(firstCompleted.id);
          setSelectedTranscript(firstCompleted);
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete transcript';
      alert(errorMessage);
      console.error('Failed to delete transcript:', error);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="text-gray-600 text-lg">Loading transcripts...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="flex justify-between items-center px-4 sm:px-6 lg:px-8 py-4 bg-white border-b border-gray-200 shadow-sm">
        <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          {APP_TITLE}
        </h1>
        <div className="flex items-center gap-3">
          {installers.map((inst) => (
            <button
              key={inst.id}
              onClick={() => handleDownloadDesktopApp(inst.id)}
              disabled={downloadingId === inst.id}
              className="px-4 py-2 bg-[#1a73e8] hover:bg-[#185abc] text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base font-medium shadow-md hover:shadow-lg flex items-center gap-2"
              title={inst.fileName}
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M12 3a1 1 0 0 1 1 1v9.586l3.293-3.293a1 1 0 0 1 1.414 1.414l-5 5a1 1 0 0 1-1.414 0l-5-5A1 1 0 0 1 7.707 10.293L11 13.586V4a1 1 0 0 1 1-1z"
                />
                <path
                  fill="currentColor"
                  d="M5 19a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1z"
                />
              </svg>
              <span>
                {downloadingId === inst.id ? 'Downloading…' : getInstallerButtonLabel(inst.platform)}
              </span>
            </button>
          ))}
          <button
            onClick={() => navigate('/profile')}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors text-sm sm:text-base font-medium shadow-md hover:shadow-lg flex items-center gap-2"
            title="User Profile"
          >
            <span>👤</span>
            <span className="hidden sm:inline">Profile</span>
          </button>
          <button
            onClick={logout}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm sm:text-base font-medium shadow-md hover:shadow-lg"
          >
            Logout
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <ResizableSplitter
          left={
            <TranscriptList
              transcripts={transcripts}
              selectedTranscriptId={selectedTranscriptId}
              onSelectTranscript={handleSelectTranscript}
              onDeleteTranscript={handleDeleteTranscript}
            />
          }
          right={
            <TranscriptView
              transcript={selectedTranscript}
              version={selectedTranscriptVersion}
              onTranscriptUpdate={(updatedTranscript) => {
                setSelectedTranscript(updatedTranscript);
                setTranscripts((prevTranscripts) =>
                  prevTranscripts.map((t) => (t.id === updatedTranscript.id ? updatedTranscript : t))
                );
              }}
            />
          }
          initialLeftWidth={30}
          minLeftWidth={20}
          maxLeftWidth={60}
        />
      </div>
    </div>
  );
};

export default Dashboard;
